import React from 'react';
import { differentiationData } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';
import ConceptDiagram from './graphics/ConceptDiagram';

const conceptTypes = ['operating-system', 'pattern-mind', 'thought-code', 'presence-architecture'];

export const DifferentiationSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [gridRef, gridVisible] = useScrollAnimation({ threshold: 0.1 });
  const [closingRef, closingVisible] = useScrollAnimation({ threshold: 0.3 });

  return (
    <section id="work" className="bg-[#fffef2] py-24 lg:py-32 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{ 
        backgroundImage: 'radial-gradient(circle at 1px 1px, #333 1px, transparent 0)',
        backgroundSize: '48px 48px'
      }} />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`max-w-3xl mb-16 animate-on-scroll ${headerVisible ? 'is-visible' : ''}`}
        >
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#333333] leading-tight mb-8">
            {differentiationData.sectionTitle}
          </h2>
          <p className="text-sm text-[#5a7fa8] uppercase tracking-wider mb-6">
            {differentiationData.intro}
          </p>
          <p className="text-base md:text-lg text-[#666666] leading-relaxed">
            {differentiationData.explanation}
          </p>
        </div>

        {/* Concepts Grid */}
        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 gap-6 lg:gap-8 mb-16 stagger-children ${gridVisible ? 'is-visible' : ''}`}
        >
          {differentiationData.concepts.map((concept, index) => (
            <div
              key={index}
              className="group relative p-8 bg-gradient-to-br from-[#1a2435] to-[#0f1a2a] border border-[#2a3a4d] transition-all duration-500 hover:border-[#5a7fa8]/50 hover-lift overflow-hidden"
            >
              {/* Hover glow effect */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#5a7fa8] opacity-0 group-hover:opacity-10 blur-[40px] transition-opacity duration-500" />
              
              <div className="flex items-start gap-6 relative z-10">
                {/* Concept Diagram */}
                <div className="w-24 h-24 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110">
                  <ConceptDiagram type={conceptTypes[index]} />
                </div>
                
                <div>
                  <h3 className="font-serif text-xl text-[#f5f5f0] mb-3 group-hover:text-[#c9a87c] transition-colors duration-300">
                    {concept.term}
                  </h3>
                  <p className="text-sm text-[#6b7c93] leading-relaxed group-hover:text-[#b8c5d4] transition-colors duration-300">
                    {concept.description}
                  </p>
                </div>
              </div>
              
              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-gradient-to-r from-[#c9a87c] to-[#5a7fa8] group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* Closing Note */}
        <div 
          ref={closingRef}
          className={`text-center fade-in ${closingVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-6">
            <span className="w-16 h-px bg-gradient-to-r from-transparent via-[#c9a87c] to-transparent" />
            <p className="font-serif text-lg md:text-xl text-[#333333] tracking-wide">
              {differentiationData.closingNote}
            </p>
            <span className="w-16 h-px bg-gradient-to-r from-transparent via-[#5a7fa8] to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};
